import React, { useState } from 'react';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import { Card, CardContent } from '../components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { heroImages, brands } from '../data/mock';
import { Apple, Zap, Battery, X, ChevronDown } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useCarousel } from '../hooks/useCarousel';

// Fallback images
const fallbackImages = [
  '/assets/images/nutrition-1.jpg',
  '/assets/images/nutrition-2.jpg',
];

const NutritionPage = () => {
  const { currentTheme } = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const { images: dbImages } = useCarousel('nutrition');
  
  // Use database images or fallback
  const nutritionCarouselImages = dbImages.length > 0 ? dbImages : fallbackImages;

  const toggleBrand = (brandName) => {
    setExpandedBrand(expandedBrand === brandName ? null : brandName);
  };
  
  const timings = [
    {
      icon: Apple,
      title: 'Avant l\'effort',
      description: 'La composition et le moment du dernier repas avant une activité sportive doivent être soigneusement planifiés. Un choix judicieux permet d\'éviter, pendant l\'effort, les désagréments tels que troubles digestifs, hypoglycémie réactionnelle, fringales ou coups de fatigue, souvent liés à une stratégie nutritionnelle inadaptée.',
      cardStyle: 'dark' // Fond gris foncé
    },
    {
      icon: Zap,
      title: 'Pendant l\'effort',
      description: 'Pendant la pratique sportive, une alimentation régulière est essentielle pour maintenir une bonne hydratation et apporter l\'énergie nécessaire à la performance physique et mentale.\n\nLes produits diététiques que nous vous proposons sont spécialement conçus pour être consommés pendant cette phase de forte dépense énergétique, afin de soutenir votre endurance et votre concentration tout au long de l\'effort.',
      cardStyle: 'primary' // Fond couleur primaire (vert)
    },
    {
      icon: Battery,
      title: 'Après l\'effort',
      description: 'Souvent négligée, la phase de récupération mérite pourtant une attention toute particulière. C\'est elle qui permet à l\'organisme de se régénérer efficacement après l\'effort et de préparer les prochaines performances.\n\nEssentielle lors d\'enchaînements d\'entraînement intensif ou après une grosse compétition, une récupération bien gérée favorise la réparation musculaire, le rechargement énergétique et limite l\'apparition de la fatigue.',
      cardStyle: 'light' // Fond blanc avec bordure
    }
  ];

  const getCardStyles = (cardStyle) => {
    if (currentTheme === 'ral6039') {
      switch (cardStyle) {
        case 'dark':
          return {
            bg: 'bg-[#4F5358]',
            text: 'text-white',
            subtext: 'text-gray-200',
            iconBg: 'bg-white/20',
            iconColor: 'text-white'
          };
        case 'primary':
          return {
            bg: 'bg-[#B3C43E]',
            text: 'text-white',
            subtext: 'text-white/90',
            iconBg: 'bg-white/30',
            iconColor: 'text-white'
          };
        case 'light':
          return {
            bg: 'bg-white border-2 border-[#4F5358]',
            text: 'text-gray-900',
            subtext: 'text-gray-600',
            iconBg: 'bg-[#4F5358]',
            iconColor: 'text-white'
          };
        default:
          return {};
      }
    } else {
      // Thème Émeraude
      switch (cardStyle) {
        case 'dark':
          return {
            bg: 'bg-gray-800',
            text: 'text-white',
            subtext: 'text-gray-200',
            iconBg: 'bg-white/20',
            iconColor: 'text-white'
          };
        case 'primary':
          return {
            bg: 'bg-emerald-600',
            text: 'text-white',
            subtext: 'text-emerald-50',
            iconBg: 'bg-white/20',
            iconColor: 'text-white'
          };
        case 'light':
          return {
            bg: 'bg-white border-2 border-gray-300',
            text: 'text-gray-900',
            subtext: 'text-gray-600',
            iconBg: 'bg-emerald-600',
            iconColor: 'text-white'
          };
        default:
          return {};
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* Lightbox Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-2 right-2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Nutrition sportive"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>

      <HeroSection
        image={heroImages.nutrition}
        title="La Diététique"
        subtitle="L'équilibre alimentaire du sportif"
        height="h-[50vh]"
      />

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-gray-600 leading-relaxed space-y-4 text-justify">
            <p>
              L'équilibre alimentaire repose sur l'adaptation entre les apports et les besoins nutritionnels propres à chaque individu. Chez le sportif, cet équilibre est souvent plus difficile à atteindre avec une alimentation classique, en raison des besoins énergétiques accrus liés à la pratique régulière d'une activité physique.
            </p>
            <p>
              Une alimentation variée et équilibrée reste la base indispensable pour couvrir les besoins en glucides, protéines, lipides, vitamines et minéraux. L'hydratation joue également un rôle essentiel selon la température et l'intensité de l'effort.
            </p>
            <p>
              Sur ce socle d'alimentation saine, la nutrition sportive vient en complément pour optimiser les performances. En choisissant des produits diététiques adaptés, vous apportez à votre organisme une nutrition de qualité, parfaitement calibrée pour soutenir vos entraînements et vos compétitions.
            </p>
            <p>
              Chez Foulées Vertes, nous vous proposons une sélection de produits diététiques de l'effort spécialement conçus pour répondre aux besoins nutritionnels des sportifs. Faites de votre alimentation un véritable levier de succès !
            </p>
          </div>
        </div>
      </section>

      {/* Timing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="Les 3 temps de l'effort"
            subtitle="Une nutrition adaptée à chaque moment clé"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {timings.map((timing) => {
              const IconComponent = timing.icon;
              const styles = getCardStyles(timing.cardStyle);
              return (
                <div 
                  key={timing.title} 
                  className={`rounded-2xl p-8 ${styles.bg} transition-transform hover:scale-[1.02] duration-300`}
                >
                  <div className={`w-14 h-14 ${styles.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                    <IconComponent className={`w-7 h-7 ${styles.iconColor}`} />
                  </div>
                  <h3 className={`font-neuropolitical font-bold text-2xl mb-4 ${styles.text}`}>
                    {timing.title}
                  </h3>
                  <p className={`leading-relaxed whitespace-pre-line text-sm ${styles.subtext} text-justify`}>
                    {timing.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {nutritionCarouselImages.map((image, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div 
                    className="overflow-hidden rounded-xl shadow-lg cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image}
                      alt={`Nutrition sportive ${index + 1}`}
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 lg:-left-12" />
            <CarouselNext className="-right-4 lg:-right-12" />
          </Carousel>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="Nos Marques Nutrition"
            subtitle="Une sélection de produits de qualité pour vos besoins"
          />
          <div className="mt-10">
            <img
              src="/assets/images/nutrition-marques.jpg"
              alt="Nos marques nutrition"
              className="w-full max-w-4xl mx-auto rounded-xl shadow-lg"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 bg-emerald-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-neuropolitical font-bold mb-6">Conseils de nos experts</h2>
          <p className="text-emerald-100 text-lg leading-relaxed">
            Notre équipe peut vous conseiller sur la nutrition adaptée à vos objectifs 
            et à votre pratique. N'hésitez pas à nous poser vos questions en magasin.
          </p>
        </div>
      </section>
    </div>
  );
};

export default NutritionPage;
